import { useContext, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { StyleContext } from "../providers/StyleContextProvider";
import { AuthContext } from "../providers/AuthContextProvider";
import { LoggedRecruiter } from "../models/LoggedRecruiter";
import { Button } from "../components/Shared/Buttons/Button";
import { RecruiterMenu } from "../components/Shared/RecruiterMenu";
import { ReactComponent as Plus } from '../assets/svg/plus.svg';
import { useMediaQuery } from "react-responsive";
import { RecruiterProfileArea } from "../components/Shared/RecruiterProfileArea";
import { ReactComponent as LeftArrow } from "../assets/svg/left_arrow.svg";
import { SingleSelect } from "../components/Shared/SingleSelect";
import { SelectInputs } from "../models/SelectInputs";
import { getAllUsersList, removeRecruiter, sendRequestToRecruiter } from "../services/RecruiterService";
import useErrorPage from "../hooks/useErrorPage";
import { Recruiter } from "../models/Recruiter";
import { Pagination } from "../components/Shared/Pagination";
import { FeedbackCard } from "../components/Shared/FeedbackCard";
import { CommonTexts as Texts } from '../../src/helpers/LayoutTexts';
import { Footer } from "../components/Shared/Footer";
import { useAlert } from "../hooks/useAlert";
import { ResponseModel } from "../models/ResponseModel";
import { PopUpWindow } from "../components/Shared/PopUpWindow";
import { UserListLoader } from "../components/Loaders/UserListLoader";
import { validateEmail } from "../helpers/Common";
import { TextInput } from "../components/Shared/Inputs/TextInput";

interface RecruiterImage {
  recruiterId: number;
  imageBase64: string | null;
};

interface EmailValidation {
    valid?: boolean;
    invalid?: boolean;
    invalidMessage?: string;
}

export const UsersPage: React.FC = () => {
    const navigate = useNavigate();
    const styleContext = useContext(StyleContext);
    const authContext = useContext(AuthContext);
    const [searchParams] = useSearchParams();  
    const recruiter: LoggedRecruiter | null = authContext.getLoggedRecruiter();
    const [styles, setStyles] = useState<any>(styleContext.getComponentStyle("usersPage")); 
    const isMobile = useMediaQuery({ query: "(max-width: 786px)" }); 
    const [totalPages, setTotalPages] = useState<number>();
    const [loading, setLoading] = useState<boolean>(true);
    const [toErrorPage] = useErrorPage(); 
    const [usersList, setUsersList] = useState<Recruiter[]>([]); 
    const page: number = searchParams.get("page") === null ? 1 : +searchParams.get("page")!;
    const [hoveredIndex, setHoveredIndex] = useState<number>(-1);
    const [imageList, setImageList] = useState<RecruiterImage[]>([]);
    const pageSize: number = searchParams.get("pageSize") === null ? 20 : +searchParams.get("pageSize")!;
    const {Alert, openAlert} = useAlert();
    const [openAddUserDialog, setOpenAddUserDialog] = useState<boolean>(false);
    const [email, setEmail] = useState<string>("");
    const [emailValidation, setEmailValidation] = useState<EmailValidation>({});
    const [openRemoveUserDialog, setOpenRemoveUserDialog] = useState<boolean>(false);
    const [removeUserContent, setRemoveUserContent] = useState<React.ReactNode>(null);
    const [openRemovalWarningDialog, setOpenRemovalWarningDialog] = useState<boolean>(false);
    const [removalWarningContent, setRemovalWarningContent] = useState<React.ReactNode>(null);
    const [searchTerm, setSearchTerm] = useState<string>("");
    const [filteredUsersList, setFilteredUsersList] = useState<Recruiter[]>([]);

    const perPageSelectInputs: SelectInputs = {
        default: pageSize.toString(),
        options: [          
            {"value": 15, "option": "15"},
            {"value": 20, "option": "20"},
            {"value": 30, "option": "30"},
            {"value": 60, "option": "60"}
        ]
    }

    useEffect(() => {                      
        getAllUsersListByOrganization();        
    }, []);

    useEffect(() => {
        if (searchTerm === "") {
            setFilteredUsersList(usersList);
        } else {
            const filteredRecruiter = usersList.filter(user => {
                const fullName = `${user.forename} ${user.surname}`.toLowerCase();
                const email = user.email.toLowerCase();
                const search = searchTerm.toLowerCase();
                return fullName.includes(search) || email.includes(search);
            });
            setFilteredUsersList(filteredRecruiter);
        }
    }, [searchTerm, usersList]);

    useEffect(() => {                      
        if (filteredUsersList.length > 0) {
            const totalPageCount = Math.ceil(filteredUsersList.length / pageSize);
            setTotalPages(totalPageCount);
        } else {
            setTotalPages(1);
        }
    }, [filteredUsersList, pageSize]);

    const handleSearchSubmit = (search: string) => {
        setSearchTerm(search);
        searchParams.set("page", "1");
        const newUrl = `?${searchParams}`;
        navigate(newUrl, { replace: true });
    }

    const setPageSize = (selectedPageSize: number) => {
        searchParams.set("page", "1");
        searchParams.set("pageSize", selectedPageSize.toString());
        const newUrl = `?${searchParams}`;
        window.scrollTo({top: 0});
        navigate(newUrl);
    }

    const handlePagination = (selectedPage: number) => {
        searchParams.set("page", selectedPage.toString());
        const newUrl = `?${searchParams}`;
        window.scrollTo({top: 0});
        navigate(newUrl);      
    }

    const displayAddUserDialog = () => {        
        setOpenAddUserDialog(true);
    }

    const closeAddUserDialog = () => {
        setEmail("");
        setEmailValidation({});
        setOpenAddUserDialog(false); 
    }

    const closeRemoveUserDialog = () => {
        setOpenRemoveUserDialog(false); 
    }

    const closeRemovalWarning = () => {
        setOpenRemovalWarningDialog(false); 
    }

    const displayRemoveUserDialog = (recruiterId: number) => {
        let content: any = <>
            <h1 style={styles.dialog.title}>{"Are you sure?"}</h1>
            <p style={styles.dialog.message}>{"Deleting this recruiter account will remove it from the organization."}</p>
            <div className="center-content" style={styles.deleteButtons}>
                <Button style={{...styles.dialog.button, ...styles.dialog.button.red}} onClick={() => removeRecruiterFromOrganization(recruiterId)}>{"YES"}</Button>
                <Button style={{...styles.dialog.button, ...styles.dialog.button.borderBlue}} onClick={closeRemoveUserDialog}>{"NO"}</Button>                
            </div>
        </>
        setRemoveUserContent(content);        
        setOpenRemoveUserDialog(true);      
    }

    const showRemovalWarning = () => {
        let content: any = <>
            <p style={styles.dialog.message}>{`This recruiter has active job postings linked to their email.`}</p>
            <p style={styles.dialog.secondaryMessage}>{`To remove them from the organization, please transfer those job postings to another recruiter's email.`}</p>
            <div className="center-content" style={styles.deleteButtons}>            
                <Button style={{...styles.dialog.button, ...styles.dialog.button.ash}} onClick={closeRemovalWarning}>{"OK"}</Button>
            </div>
        </>
        setRemovalWarningContent(content); 
        setOpenRemovalWarningDialog(true);    
    }

    const handleBackButton = () => {
        navigate('/recruiter/vacancy');
    }

    const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setEmail(value);
        if (emailValidation.invalid) {
            validateEmailInput(value);
        }
    }

    const handleEmailBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        validateEmailInput(e.target.value);
    }

    const validateEmailInput = (value: string) => {
        if (value === "") {
            setEmailValidation({ valid: false, invalid: true, invalidMessage: "Email is required" });
        } else if (!validateEmail(value)) {
            setEmailValidation({ valid: false, invalid: true, invalidMessage: "Invalid email address!" });
        } else {
            setEmailValidation({ valid: true, invalid: false });
        }
    }

    async function addUser() {   
        validateEmailInput(email); 
        if (email === "" || !validateEmail(email)) {
            openAlert("error", "Please enter a valid email address");
            return;
        }  
        await sendRequestToRecruiter(email, recruiter!.recruiterId, recruiter!.token)
        .then((response: ResponseModel) => {                           
            if(response.status) {
                openAlert("success", response.message);
                getAllUsersListByOrganization(); 
            }
            else {
                openAlert("error", response.message);
            }
            setEmail("");
            setEmailValidation({});
            setOpenAddUserDialog(false); 
        })    
        .catch((error) => {            
            toErrorPage(error);
        });        
    }

    async function getAllUsersListByOrganization() {
        await getAllUsersList(recruiter!.organizationId, recruiter!.recruiterId, recruiter!.token)
        .then((response) => {
            setUsersList(response.recruiters);
            setImageList(response.recruiterImages);
            window.scrollTo({top: 0, behavior: 'smooth'});
        })
        .catch((error) => {
            toErrorPage(error);            
        })
        .finally(() => {
            setLoading(false);
        });
    }

    async function removeRecruiterFromOrganization(recruiterId: number) {
        setOpenRemoveUserDialog(false);
        await removeRecruiter(recruiterId, recruiter!.recruiterId, recruiter!.token)
        .then((response: ResponseModel) => {
            if (response.status){
                openAlert("success", "Recruiter removed successfully");
                setUsersList(prev => prev.filter(u => u.recruiterId !== recruiterId));
            }
            else {
                if (response.message === "This recruiter have active or pending jobs.") {
                    showRemovalWarning();
                }
                else {
                    openAlert("error", response.message);
                }
            }
        })
        .catch((error) => {
            toErrorPage(error);            
        });
    }

    return (
        <>
            <div style={styles.mainWrapper}>
                <div style={styles.leftWrapper}>
                    <RecruiterMenu>
                        <Button style={styles.newUser} onClick={() => displayAddUserDialog()}>
                            {"Add a new User"}
                            <span style={styles.newUser.iconWrapper}><Plus style={styles.newUser.icon}/></span>
                        </Button>
                    </RecruiterMenu>
                </div>
                <div style={styles.rightWrapper}>
                    {!isMobile && <RecruiterProfileArea searchEnable={true} placeholder={"Search User"} handleSearchSubmit={handleSearchSubmit}/>}
                    <div style={styles.backButton.container}>
                        <div style={styles.backButton.buttonWrapper} onClick={handleBackButton}>
                            <Button style={styles.backButton.button}><LeftArrow style={styles.backButton.icon} /></Button>
                            <span style={styles.backButton.text}>{"Back"}</span>
                        </div>
                    </div>
                    <div style={styles.titleWrapper}>
                        <div style={styles.title}>{"Users"}</div>
                        <div style={styles.addUserWrapper}>
                            <Button style={styles.addUserWrapper.addUser} onClick={() => displayAddUserDialog()}>
                                {"ADD NEW USER"}
                                <Plus style={styles.addUserWrapper.icon}/>
                            </Button>
                        </div>
                        <div style={styles.selectRow}>
                            {isMobile && <SingleSelect styles={styles.perPageSelect} label={"Rows Per Page"} selectInputs={perPageSelectInputs} getSelected={setPageSize} />}
                        </div>
                    </div>
                    <div style={styles.userList}>
                        {loading ? <UserListLoader/> :
                        <> 
                            {filteredUsersList.length > 0 ? <>
                                {filteredUsersList.slice((page - 1) * pageSize, page * pageSize).map((user, index) => {
                                    const imageObj = imageList.find(img => img.recruiterId === user.recruiterId);
                                    const recruiterProfilePictureURL = imageObj?.imageBase64 && `data:image/jpeg;base64,${imageObj.imageBase64}`;
                                    return (
                                        <div 
                                            key={index} 
                                            style={{...styles.userCard, 
                                                ...(index + 1 !== hoveredIndex && styles.userCard.bottomBorder),  
                                                ...(hoveredIndex === index && styles.userCard.hovered)}}
                                            onMouseEnter={() => setHoveredIndex(index)}
                                            onMouseLeave={() => setHoveredIndex(-1)}
                                        >
                                            <div style={styles.userCard.userProfile}>
                                                <img src={recruiterProfilePictureURL ? recruiterProfilePictureURL : Texts.defaultUser} style={styles.userCard.userAvatar} alt={user.recruiterId !== 0 ? `${user.forename} ${user.surname}` : "Pending user"}/>
                                                <div style={styles.userCard.details}>
                                                    <div style={styles.userCard.nameWrapper}>
                                                        {(user.recruiterId !== 0) && <div style={styles.userCard.name}>{`${user.forename} ${user.surname}`}</div>}
                                                        {!isMobile && <div style={{...styles.userCard.role,
                                                                ...(user.jobTitle === "Recruiter->SignUp" && styles.userCard.role.red), 
                                                                ...(user.jobTitle === "Recruiter->SubLogin" && styles.userCard.role.blue),
                                                                ...(user.jobTitle !== "Recruiter->SignUp" && user.jobTitle !== "Recruiter->SubLogin" && styles.userCard.role.orange)}}
                                                        >
                                                            {user.jobTitle === "Recruiter->SignUp"
                                                                ? "Admin User"
                                                                : user.jobTitle === "Recruiter->SubLogin"
                                                                ? "Sub-login User"
                                                                : "Invitation-accepted User"}
                                                        </div>}
                                                    </div>
                                                    <div style={styles.userCard.contactWrapper}>
                                                        {!isMobile && <div style={styles.userCard.contact}>{"Contact"}</div>}
                                                        <div style={styles.userCard.email}>{user.email}</div>
                                                    </div>
                                                    {isMobile && <div style={{...styles.userCard.role,
                                                        ...(user.jobTitle === "Recruiter->SignUp" && styles.userCard.role.red), 
                                                        ...(user.jobTitle === "Recruiter->SubLogin" && styles.userCard.role.blue),
                                                        ...(user.jobTitle !== "Recruiter->SignUp" && user.jobTitle !== "Recruiter->SubLogin" && styles.userCard.role.orange)}}
                                                    >
                                                        {user.jobTitle === "Recruiter->SignUp"
                                                            ? "Admin User"
                                                            : user.jobTitle === "Recruiter->SubLogin"
                                                            ? "Sub-login User"
                                                            : "Invitation-accepted User"}
                                                    </div>}
                                                    <div style={styles.userCard.statusWrapper}>
                                                        {(user.isOrganizationAdmin) && <div style={{...styles.userCard.status, ...styles.userCard.admin}}>{"Admin"}</div>}
                                                        {(user.recruiterId === 0) && <div style={{...styles.userCard.status, ...styles.userCard.pending}}>{"Pending"}</div>}
                                                        {(!user.isOrganizationAdmin && user.recruiterId !== 0) && <Button style={{...styles.userCard.status, ...styles.userCard.removeButton}} onClick={() => displayRemoveUserDialog(user.recruiterId)}>{"Remove"}</Button>}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )
                                })}
                                <div style={styles.paginationWrapper}>
                                    {!isMobile && <SingleSelect styles={styles.perPageSelect} label={"Rows Per Page"} selectInputs={perPageSelectInputs} getSelected={setPageSize} />}
                                    {(totalPages! > 1) && <Pagination page={page} totalPages={totalPages!} handlePagination={handlePagination} extendedStyles={styles.pagination} />}
                                </div> 
                            </> : <FeedbackCard
                                    styles={styles.noUsers}
                                    message={"There are no users available."}
                                />
                            }    
                        </>}
                    </div>    
                </div>
            </div>
            <PopUpWindow styles={styles.userDialog} open={openAddUserDialog} toggle={closeAddUserDialog} closeCircle={true}>
                <>                
                    <h1 style={styles.dialog.title}>{"Add New User"}</h1>
                    <div style={styles.dialog.emailWrapper}>
                        <div style={styles.dialog.inputText}>{"Email"}</div>
                        <TextInput
                            label={'Email *'}
                            styles={styles.dialog.input} 
                            value={email}
                            name={"Email"}
                            inputType={'text'}
                            validation={emailValidation}
                            handleChange={handleEmailChange}
                            handleBlur={handleEmailBlur}
                        />
                    </div>
                    <div className="center-content" style={styles.dialog.buttons}>     
                        <Button style={{...styles.dialog.button, ...styles.dialog.button.ash}} onClick={() => closeAddUserDialog()}>{"Cancel"}</Button>                                  
                        <Button style={{...styles.dialog.button, ...styles.dialog.button.blue}} onClick={() => addUser()}>{"Send Invitation"}</Button>
                    </div>
                </> 
            </PopUpWindow>
            <PopUpWindow styles={styles.userDialog} open={openRemoveUserDialog} toggle={closeRemoveUserDialog} closeCircle={true}>
                {removeUserContent}
            </PopUpWindow>
            <PopUpWindow styles={{...styles.userDialog, ...styles.removalDialog}} open={openRemovalWarningDialog} toggle={closeRemovalWarning} closeCircle={true}>
                {removalWarningContent}
            </PopUpWindow>
            <Alert/>      
            <Footer/>
        </>
    )
}